import { Table, Tag } from "antd"
import type { TableColumnsType } from "antd"
import type { CoverageMatrix } from "@csbi/contracts"
import { tenantColor } from "@csbi/viz-core"
import { useTheme } from "../../../app/providers/theme-provider.js"
import { columnTotals, type MatrixRow } from "../../../features/coverage/matrix.js"
import { CoverageCell } from "./coverage-cell.js"

const VERTICAL_FROM = 7

/** Width of a customer column. Vertical, the cell decides (20px + padding); horizontal, the name does */
const TENANT_COL_VERTICAL = 34
const TENANT_COL_HORIZONTAL = 150
/** Height of a vertical header. At 60 deployments it is the only way to keep columns at 34px */
const HEADER_HEIGHT = 132

type TenantMeta = CoverageMatrix["tenants"][number]

export function CoverageTable({
  rows,
  tenants,
  loading,
}: {
  rows: readonly MatrixRow[]
  tenants: readonly TenantMeta[]
  loading: boolean
}) {
  const { mode } = useTheme()
  const totals = columnTotals(rows, tenants.length)
  const vertical = tenants.length >= VERTICAL_FROM

  const columns: TableColumnsType<MatrixRow> = [
    {
      title: "Metric",
      key: "metric",
      // After scrolling sideways past 60 columns, without pinning this one you cannot tell which row you are on
      fixed: "left",
      width: 260,
      render: (_: unknown, r) => (
        <span style={{ display: "block", minWidth: 0 }}>
          <span style={{ display: "block", color: "var(--csbi-ink-primary)" }}>{r.name}</span>
          {/* The id is what actually gets copied out to grep with, so it belongs on screen, not in a tooltip */}
          <code style={{ fontSize: 11, color: "var(--csbi-ink-muted)" }}>{r.id}</code>
        </span>
      ),
    },
    {
      title: "Domain",
      dataIndex: "domainLabel",
      key: "domain",
      fixed: "left",
      width: 112,
      render: (label: string) => <span style={{ color: "var(--csbi-ink-secondary)" }}>{label}</span>,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      fixed: "left",
      width: 78,
      render: (p: string) =>
        p === "must" ? (
          <Tag style={{ marginInlineEnd: 0 }}>must</Tag>
        ) : (
          <span style={{ fontSize: 12, color: "var(--csbi-ink-muted)" }}>{p === "unspecified" ? "—" : p}</span>
        ),
    },
    ...tenants.map((t, i) => ({
      title: <TenantHeader tenant={t} color={tenantColor(t.colorSlot, mode)} vertical={vertical} />,
      key: t.id,
      width: vertical ? TENANT_COL_VERTICAL : TENANT_COL_HORIZONTAL,
      align: "center" as const,
      render: (_: unknown, r: MatrixRow) => {
        const state = r.cells[i]
        // The column count and cells length are identically generated on the backend from the same tenantIds. If they ever differ, show a blank rather than
        // inventing a state — guessing `empty` is the same mistake as passing 0 off as missing
        return state ? <CoverageCell state={state} tenantName={t.displayName} /> : null
      },
    })),
    {
      title: "Linked",
      key: "okCount",
      width: 74,
      align: "right" as const,
      render: (_: unknown, r) => (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            fontSize: 12,
            color: r.okCount > 0 ? "var(--csbi-ink-secondary)" : "var(--csbi-ink-muted)",
          }}
        >
          {r.okCount}/{tenants.length}
        </span>
      ),
    },
  ]

  return (
    <Table<MatrixRow>
      size="small"
      rowKey="id"
      loading={loading}
      columns={columns}
      dataSource={rows as MatrixRow[]}
      pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (n) => `${n} metrics` }}
      // 90 rows do not fit on a screen, and a header that scrolls away leaves the columns unidentifiable
      sticky
      scroll={{ x: "max-content" }}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={3}>
            <span style={{ fontSize: 12, color: "var(--csbi-ink-secondary)" }}>
              Linked per deployment, across {rows.length} matching {rows.length === 1 ? "metric" : "metrics"}
            </span>
          </Table.Summary.Cell>
          {totals.map((t, i) => (
            <Table.Summary.Cell key={tenants[i]!.id} index={3 + i} align="center">
              <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}>{t.ok}</span>
            </Table.Summary.Cell>
          ))}
          <Table.Summary.Cell index={3 + tenants.length} align="right" />
        </Table.Summary.Row>
      )}
    />
  )
}

function TenantHeader({ tenant, color, vertical }: { tenant: TenantMeta; color: string; vertical: boolean }) {
  const label = (
    <span
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontWeight: 500,
        fontSize: 12,
        ...(vertical
          ? {
              writingMode: "vertical-rl" as const,
              // vertical-rl defaults to "read downwards with the characters rotated"; 180° gives the usual head-tilt reading
              transform: "rotate(180deg)",
              maxHeight: HEADER_HEIGHT,
            }
          : { maxWidth: TENANT_COL_HORIZONTAL - 20 }),
      }}
    >
      {tenant.displayName}
    </span>
  )

  return (
    <span
      title={`${tenant.displayName} · ${tenant.environment}${tenant.srvVersion ? ` · srv ${tenant.srvVersion}` : ""}`}
      style={{
        display: "inline-flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        maxWidth: "100%",
      }}
    >
      {/* The dot's colour comes from color_slot — the same colour as its line on the chart and its dot in the /tenants table */}
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: color, flex: "0 0 auto" }} />
      {label}
    </span>
  )
}
